import cn from "classnames";

import React, { useCallback, useMemo } from "react";

import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid,
  IconButton,
  Paper,
} from "@mui/material";

import ExpandBtn from "@/components/FlightItem/ExpandBtn";
import InfoBtn from "@/components/FlightItem/InfoBtn";
import ProductInfo from "@/components/FlightItem/ProductInfo";
import SitemapDialogBtn from "@/components/SitemapDialog";
import useThemeData from "@/hooks/useThemeData";
import { FlightItem as FlightItemType } from "@/typings";

import Banks from "../Banks";
import CabinInfo from "../CabinInfo";
import SummaryAirline from "../SummaryAirline";
import usePlatform from "../usePlatform";

interface FlightItemProps {
  data: FlightItemType;
  isExpanded: boolean;
  onExpandChange: (key: string) => void;
  onOpenOptionDialog: (data: FlightItemType) => void;
  onOpenRouteDialog?: (data: FlightItemType) => void;
  onSearch?: (data: FlightItemType) => void;
  showLastSeen?: boolean;
  showSearchBtn?: boolean;
  needRouteBtn?: boolean;
}

const FlightItem = ({
  isExpanded,
  data,
  onExpandChange,
  onOpenOptionDialog,
  onOpenRouteDialog,
  showLastSeen = false,
  showSearchBtn,
  onSearch,
  needRouteBtn,
}: FlightItemProps) => {
  const { isMobile } = usePlatform();

  const products = useMemo(() => data?.fare?.products || [], [data]);

  const theme = useThemeData();
  const handleExpandChange = useCallback(() => {
    onExpandChange(data.hash);
  }, [data, onExpandChange]);

  const handleOpenOptionDialog = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onOpenOptionDialog(data);
    },
    [data, onOpenOptionDialog]
  );
  const handleOpenRouteDialog = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onOpenRouteDialog?.(data);
    },
    [data, onOpenRouteDialog]
  );

  const renderPc = () => {
    return (
      <Paper
        className="flex items-center w-full mt-[1px]"
        elevation={theme.isDarkMode ? 4 : 1}
        sx={{
          borderRadius: "12px",
          border: "0.5px solid rgba(194, 197, 200, 0.8)",
          // borderBottom: "0.5px rgba(194, 197, 200, 0.5)",
        }}
      >
        <div className="flex flex-1 w-full h-full">
          <SummaryAirline data={data} className="w-[40%]" />
          <Divider orientation="vertical" />
          <Banks data={data} />
          <CabinInfo data={data} />
        </div>
        <div className="w-[40px]">
          <Grid container className="w-full" rowSpacing={1} alignItems="center">
            <div>
              {needRouteBtn && (
                <Grid item className="!pl-[0px] !pt-[12px]" sm={0.5}>
                  <IconButton onClick={handleOpenRouteDialog}>
                    <AutoAwesomeRoundedIcon color="primary" />
                  </IconButton>
                </Grid>
              )}
              <Grid item className="!pl-[0px]" sm={0.5}>
                <InfoBtn onClick={handleOpenOptionDialog} isExpanded={isExpanded} />
              </Grid>
              <Grid item className="!pl-[0px]" sm={0.5}>
                <ExpandBtn onClick={handleExpandChange} isExpanded={isExpanded} />
              </Grid>
            </div>
          </Grid>
        </div>
      </Paper>
    );
  };

  const renderMobile = () => {
    return (
      <Paper
        className="w-full"
        elevation={theme.isDarkMode ? 4 : 1}
        sx={{
          borderRadius: "4px",
          border: "0.5px solid rgba(194, 197, 200, 0.8)",
          // borderBottom: "0.5px rgba(194, 197, 200, 0.5)",
        }}
      >
        <div className="w-full p-[10px] !pb-[0px]">
          <SummaryAirline data={data} />
          <div className="mt-[10px] mb-[8px] flex justify-between">
            <Banks data={data} />

            <div className="flex-col justify-center items-center">
              <div className="flex justify-center relative bottom-[-5px]">
                <InfoBtn onClick={handleOpenOptionDialog} isExpanded={isExpanded} />
                <ExpandBtn onClick={handleExpandChange} isExpanded={isExpanded} />
              </div>
              <SitemapDialogBtn data={data} isMobile={isMobile} />
            </div>
          </div>
          <CabinInfo data={data} />
        </div>
      </Paper>
    );
  };

  return (
    <Accordion
      expanded={isExpanded}
      onChange={handleExpandChange}
      TransitionProps={{ unmountOnExit: true }}
      sx={{
        boxShadow: isExpanded ? "1" : "0",
        borderRadius: isExpanded ? "4px" : "0px",
        border: (theme) => `0px solid ${theme.palette.divider}`,
        "&::before": {
          display: "none",
        },
      }}
    >
      <AccordionSummary
        id={`${data.hash}`}
        key={`${data.hash}`}
        className={cn("!px-0", "relative")}
        onClick={handleExpandChange}
        sx={{
          "& .MuiAccordionSummary-content": {
            margin: "0 0 12px 0",
          },
        }}
      >
        {isMobile ? renderMobile() : renderPc()}
      </AccordionSummary>

      <AccordionDetails className="!pt-[15px] !pb-[20px]">
        {products.map((product, i) => (
          <ProductInfo
            key={i}
            data={data}
            product={product}
            isLast={i === products.length - 1}
            showCabin={false}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

export default React.memo(FlightItem);
